import { produce } from "immer";
import { AuthenticationActions } from "../../authentication/authentication.store";
import { IPayloadAction } from "../../../shared/shared.interfaces";
import { IIdentityDocument } from "../../../shared/models/identity.model";
import { IUpdateIdentityRequest } from "./identity.interfaces";

export const IdentityActions = {
  types: {
    FETCHING_DOCUMENT: "FETCHING_DOCUMENT",
    FETCH_DOCUMENT_SUCCESS: "FETCH_DOCUMENT_SUCCESS",
    FETCH_DOCUMENT_ERROR: "FETCH_DOCUMENT_ERROR",
    ADD_DOCUMENT: "ADD_DOCUMENT",
    EDIT_DOCUMENT: "EDIT_DOCUMENT",
    REMOVE_DOCUMENT: "REMOVE_DOCUMENT",
    ENABLE_DOCUMENT: "ENABLE_DOCUMENT",
    DISABLE_DOCUMENT: "DISABLE_DOCUMENT",
    SELECT_DOCUMENT: "SELECT_DOCUMENT",
  },
  creators: {
    fetchingDocumentsAction: () => ({
      type: IdentityActions.types.FETCHING_DOCUMENT,
    }),

    fetchDocumentsSuccessAction: (users: IIdentityDocument[]) => ({
      type: IdentityActions.types.FETCH_DOCUMENT_SUCCESS,
      payload: {
        users,
      },
    }),

    fetchDocumentsErrorAction: () => ({
      type: IdentityActions.types.FETCH_DOCUMENT_ERROR,
    }),

    addIdentityAction: (user: IIdentityDocument) => ({
      type: IdentityActions.types.ADD_DOCUMENT,
      payload: {
        user,
      },
    }),

    editDocumentsAction: (urn: string, values: IUpdateIdentityRequest) => ({
      type: IdentityActions.types.EDIT_DOCUMENT,
      payload: {
        urn,
        values,
      },
    }),

    removeDocumentsAction: (username: string) => ({
      type: IdentityActions.types.REMOVE_DOCUMENT,
      payload: {
        username,
      },
    }),

    enableDocumentsAction: (username: string) => ({
      type: IdentityActions.types.ENABLE_DOCUMENT,
      payload: {
        username,
      },
    }),

    disableDocumentsAction: (username: string) => ({
      type: IdentityActions.types.DISABLE_DOCUMENT,
      payload: {
        username,
      },
    }),

    selectDocument: (user: IIdentityDocument) => ({
      type: IdentityActions.types.SELECT_DOCUMENT,
      payload: {
        user,
      },
    }),
  },
};

export interface IIDentityDocumentsReducerInterface {
  users: IIdentityDocument[];
  isLoadingUsers: boolean;
  selectedUser: IIdentityDocument | null;
}

const initialState: IIDentityDocumentsReducerInterface = {
  isLoadingUsers: false,
  selectedUser: null,
  users: [],
};

export const identitiesReducer = produce((draft: IIDentityDocumentsReducerInterface, action: IPayloadAction<any>) => {
  switch (action.type) {
    case IdentityActions.types.FETCHING_DOCUMENT:
      draft.isLoadingUsers = true;
      return;

    case IdentityActions.types.FETCH_DOCUMENT_SUCCESS: {
      draft.isLoadingUsers = false;
      const docs = action.payload?.users;

      if (Array.isArray(docs)) {
        draft.users = docs.sort((a, b) => (a.urn && b.urn ? a.urn.localeCompare(b.urn) : -1));
      } else {
        draft.users = [];
        console.error("Expected users to be an array:", docs);
      }

      return;
    }

    case IdentityActions.types.FETCH_DOCUMENT_ERROR:
      draft.isLoadingUsers = false;
      return;

    case IdentityActions.types.ADD_DOCUMENT: {
      const user = action.payload.user;
      let newUser = { ...user };

      if (user.en && user.en.length >= 2) {
        newUser.documentType = `_${user.en.slice(0, 2).toUpperCase()}`;
      }

      draft.users = [...draft.users, newUser];
      return;
    }

    case IdentityActions.types.REMOVE_DOCUMENT:
      const { username } = action.payload;
      draft.users = draft.users.filter(u => u.urn !== username);
      return;

    case IdentityActions.types.SELECT_DOCUMENT: {
      draft.selectedUser = action.payload.user;
      return;
    }

    case IdentityActions.types.EDIT_DOCUMENT: {
      const userIdx = draft.users.findIndex(u => u.urn === action.payload.urn);
      if (userIdx > -1) {
        draft.users[userIdx] = {
          ...draft.users[userIdx],
          ...action.payload.values,
        };
      }
      return;
    }

    case AuthenticationActions.types.LOGOUT:
      return initialState;

    default:
      return draft;
  }
}, initialState);
